import { defineStore } from 'pinia'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { klona } from 'klona'
import { uuid } from '@/services/uuid'
import api from '@/services/axiosApi'
import { Config } from '@/types/config'
import { Team } from '@/types/team'
import { AnalyticsEventTypes } from '@/services/analytics'
import { useNotificationStore } from '@/stores/notificationStore'
import { repairQuestions } from '@/modules/segment/services/segmentActions'
import { useConfigChanges } from '@/composables/useConfigChanges'
import { widgetHasVisibilityErrors } from '@/modules/widget/services/widgetUtilities'
import { isValidTurnstileSiteKey, isValidTurnstileSecretKey } from '@/modules/settings/services/settingsUtilities'

export enum PublishState {
  Loading,
  Published,
  Dirty,
  Saving,
  Saved
}

let tabId: string

export const useConfigStore = defineStore('config', () => {
  tabId = uuid()
  const { hasChanges: hasConfigChanges } = useConfigChanges()

  const fullResponse = ref(null)
  const currentTeamPid = ref<number | 'demo' | null>(null)
  const config = ref<Config | null>(null)
  const snapshots = ref<[]>([])
  const teamMembers = ref<
    {
      id: number
      name: string
      email: string
      membership: { role: string }[]
    }[]
  >([])
  const currentTeam = ref<Team | null>(null)
  const lastSavedConfig = ref<Config | null>(null)
  const lastChecksum = ref(null)
  const publishState = ref<PublishState>(PublishState.Loading)
  const savesMade = ref(0)
  const isDirty = ref(false)
  const lastMeaningfulSaveTime = ref(0) // Timestamp of last save with actual changes

  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['config', currentTeamPid],
    queryFn: () =>
      api
        .get(`/api/${currentTeamPid.value}/configs/current`)
        .then((res) => res.data),
    enabled: () => !!currentTeamPid.value,
    retry: false
  })

  watch(currentTeamPid, (newVal, oldVal) => {
    // Skip Echo connections in demo mode
    if (oldVal && oldVal !== 'demo') {
      Echo.leave(`config-changes.${oldVal}`)
    }
    if (newVal && newVal !== 'demo') {
      Echo.private(`config-changes.${newVal}`).listen(
        'DraftConfigUpdated',
        (data) => {
          if (data.checksum !== lastChecksum.value) {
            configQuery.refetch()
          }
          if (data.message) {
            const notificationStore = useNotificationStore()
            notificationStore.addNotification(data.message)
          }
        }
      )
    }
  })

  const configAsString = computed(() => {
    return JSON.stringify(config.value)
  })

  const owner = computed(() => {
    return fullResponse.value?.owner
  })

  const loadSnapshotMutation = useMutation({
    mutationFn: (configId) =>
      api
        .post(`/api/${currentTeamPid.value}/configs/load_snapshot/${configId}`)
        .then((res) => res.data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['snapshots'] })
    }
  })
  const deleteSnapshotMutation = useMutation({
    mutationFn: (configId) =>
      api
        .delete(`/api/${currentTeamPid.value}/configs/${configId}`)
        .then((res) => res.data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['snapshots'] })
    }
  })

  watchEffect(() => {
    if (configQuery.data.value) {
      fullResponse.value = klona(unref(configQuery.data))
      lastSavedConfig.value = klona(fullResponse.value.draft)

      if (!lastSavedConfig.value) {
        publishState.value = PublishState.Published
        isDirty.value = false
      } else {
        publishState.value = PublishState.Saved
        // Check if draft meaningfully differs from stable using composable
        const draft = fullResponse.value.draft
        const stable = fullResponse.value.stable
        isDirty.value = hasConfigChanges(draft, stable)
      }

      lastChecksum.value = fullResponse.value.checksum
      const configData = fullResponse.value?.draft || fullResponse.value?.stable
      config.value = klona({ ...unref(configData), id: fullResponse.value?.id })

      currentTeam.value = klona(fullResponse.value.team)
      teamMembers.value = klona(fullResponse.value.members)

      // Repair questions to ensure isQuestion flag is set
      if (config.value) {
        nextTick(() => {
          repairQuestions()
        })
      }
    }
  })

  watch(
    config,
    (newConfig) => {
      if (newConfig) {
        debouncedUpdateConfig(newConfig)
      }
    },
    { deep: true }
  )

  const saveSnapshot = async (description: string) => {
    if (!config.value) return
    try {
      const data = await updateConfigAsync({
        config: config.value,
        snapshotDescription: description
      })
      queryClient.invalidateQueries({ queryKey: ['snapshots'] })
      // Invalidate snapshotConfig
    } catch (err) {}
  }

  const {
    mutate: updateConfig,
    mutateAsync: updateConfigAsync,
    isPending: isUpdatingConfig
  } = useMutation({
    mutationFn: (variables: {
      config: Config
      snapshotDescription?: string
    }) => {
      return api
        .post(`/api/${currentTeamPid.value}/configs/draft`, {
          data: variables.config,
          checksum: lastChecksum.value,
          tabId,
          snapshot_description: variables.snapshotDescription
        })
        .then((res) => res.data)
    },
    onSuccess: (response) => {
      //lastSavedConfig.value = klona(config.value)
      lastChecksum.value = response.data.checksum
      savesMade.value += 1
      publishState.value = PublishState.Dirty
    },
    onError: (error) => {
      console.error('Failed to save config:', error)
      const notificationStore = useNotificationStore()
      const errorMessage = error.response?.data?.message || 'Failed to save changes. Your recent changes may not have been saved.'
      notificationStore.addError(errorMessage, true)
    }
  })

  const debouncedUpdateConfig = useDebounceFn((newConfig: Config) => {
    const from = JSON.stringify(toRaw(newConfig))
    const to = JSON.stringify(toRaw(lastSavedConfig.value))

    // Check if there are meaningful changes using composable
    const hasMeaningfulChanges = hasConfigChanges(newConfig, lastSavedConfig.value)

    if (to && from !== to && hasMeaningfulChanges) {
      updateConfig({ config: newConfig })
      isDirty.value = true
      lastMeaningfulSaveTime.value = Date.now()
    } else if (to && from !== to) {
      // Still update config but don't mark as dirty if only accessedAt changed
      updateConfig({ config: newConfig })
    }
  }, 2000) // 2 second debounce time

  const publishConfigMutation = useMutation({
    mutationFn: ({ config, message }: { config: Config; message?: string }) =>
      api
        .post(`/api/${currentTeamPid.value}/configs/publish`, {
          data: config,
          checksum: lastChecksum.value,
          tabId,
          publish_message: message
        })
        .then((res) => res.data),
    onSuccess: (response) => {
      savesMade.value += 1

      // Show success notification
      const notificationStore = useNotificationStore()
      notificationStore.addNotification('Successfully published your changes!')

      // All other state updates handled by watchEffect after refetch
    },
    onError: (error) => {
      // Show error notification
      const notificationStore = useNotificationStore()
      const errorMessage = error.response?.data?.message || 'Failed to publish changes. Please try again.'
      notificationStore.addError(errorMessage, true)
    }
  })

  const publish = async (message?: string) => {
    if (!config.value) return
    await publishConfigMutation.mutateAsync({ config: config.value, message })
    // Wait for the refetch to complete so isDirty is properly recalculated
    await configQuery.refetch()
    recordAnalyticsEvent(AnalyticsEventTypes.PUBLISHED)
  }

  const validateForPublish = () => {
    let errors = []
    // are there any forms in use && we don't have an integration?
    const integrationStore = useIntegrationStore()
    const widgetStore = useWidgetStore()
    const offerStore = useOfferStore()
    if (
      !integrationStore.integratedPartner &&
      offerStore.optinOffers.some(
        (o) =>
          offerUsedBy(o).filter(
            (flow) => widgetStore.getWidgetsForOfferFunnel(flow.id).length > 0
          ).length > 0
      )
    ) {
      errors.push({
        message:
          "You have Flows in place that use opt-in forms, and you haven't yet set up an integration to an email marketing platform / CRM.",
        references: []
      })
    }
    if (
      integrationStore.integratedPartner &&
      offerStore.optinOffers.some(
        (o) => offerIsInUse(o) && !offerCanBeSubmitted(o)
      )
    ) {
      // Get the list of problematic forms
      const problematicForms = offerStore.optinOffers
        .filter((o) => offerIsInUse(o) && !offerCanBeSubmitted(o))
        .map((o) => o.name || 'Unnamed form')

      const formList = problematicForms.length <= 3
        ? problematicForms.join(', ')
        : `${problematicForms.slice(0, 3).join(', ')}, and ${problematicForms.length - 3} more`

      errors.push({
        message:
          `The following forms are in use but not configured to submit to ${integrationStore.integratedPartner.label}: ${formList}. To fix this, edit each form and use the "Action" tab to specify what should happen when the form is submitted (apply tags, add to lists, etc.).`,
        references: []
      })
    }

    if (
      !integrationStore.integratedPartner &&
      configAsString.value.includes('espplaceholder')
    ) {
      errors.push({
        message: `You have Flows or segments in place that currently reference a visitor's contact status (i.e. anonymous or known contact), but you haven't yet set up an integration with your email marketing platform or CRM.`,
        references: []
      })
    }

    // Validate widget visibility settings
    const widgetsWithVisibilityErrors = widgetStore.widgets
      .filter(widgetHasVisibilityErrors)
      .map((widget) => widget.name || 'Unnamed widget')

    if (widgetsWithVisibilityErrors.length > 0) {
      const widgetList = widgetsWithVisibilityErrors.length <= 3
        ? widgetsWithVisibilityErrors.join(', ')
        : `${widgetsWithVisibilityErrors.slice(0, 3).join(', ')}, and ${widgetsWithVisibilityErrors.length - 3} more`

      errors.push({
        message: `The following widgets have invalid visibility settings: ${widgetList}. Check that domains don't include protocols (http://), paths don't start with "/", paths don't contain domains, and paths don't include query parameters (?).`,
        references: []
      })
    }

    // Validate Cloudflare Turnstile keys if set
    const siteKey = currentTeam.value?.recaptcha_key
    const secretKey = currentTeam.value?.recaptcha_secret
    const hasBadSiteKey = siteKey && !isValidTurnstileSiteKey(siteKey)
    const hasBadSecretKey = secretKey && !isValidTurnstileSecretKey(secretKey)

    if (hasBadSiteKey || hasBadSecretKey) {
      errors.push({
        message: `Your Cloudflare Turnstile configuration appears to be invalid. Valid keys start with a digit followed by "x". You can fix this in your project settings under Bot Protection.`,
        references: []
      })
    }

    // Validate sync field conflicts (Other freeform vs Structured sync using same custom field)
    const questions = config.value?.rightAsk?.questions || []
    const dimensions = config.value?.dimensions || []

    const conflictingQuestions = questions
      .filter((question) => {
        // Only check questions that have "Other" sync configured
        const otherField = question.otherOptions?.syncToCustomField
        if (!otherField) return false

        // Find the segment group for this question
        const segmentGroup = dimensions.find((d) => d.id === question.dimensionId)
        if (!segmentGroup) return false

        // Check if structured sync uses the same field
        return segmentGroup.syncToCustomField === otherField
      })
      .map((q) => {
        const segmentGroup = dimensions.find((d) => d.id === q.dimensionId)
        return segmentGroup?.name || 'Unnamed question'
      })

    if (conflictingQuestions.length > 0) {
      const questionList = conflictingQuestions.length <= 3
        ? conflictingQuestions.join(', ')
        : `${conflictingQuestions.slice(0, 3).join(', ')}, and ${conflictingQuestions.length - 3} more`

      errors.push({
        message: `The following questions have the same custom field configured for both "Other" freeform responses and structured segment sync: ${questionList}. Using the same field for both will cause data conflicts. Please use different fields for each sync type.`,
        references: []
      })
    }

    return errors
  }

  const associatedWebsites = computed(() => {
    return fullResponse.value?.teams
  })

  return {
    publishState,
    isDirty,
    lastMeaningfulSaveTime,
    currentTeamPid,
    fullResponse,
    owner,
    config,
    configAsString,
    currentTeam,
    lastChecksum,
    savesMade,
    isLoading: configQuery.isLoading,
    isError: configQuery.isError,
    configQuery,
    saveSnapshot,
    loadSnapshotMutation,
    deleteSnapshotMutation,
    snapshots,
    isUpdatingConfig,
    publish,
    validateForPublish,
    teamMembers,
    associatedWebsites,
    debouncedUpdateConfig
  }
})
